// packages/shared/src/services/bookingService.ts
import { doc, addDoc, updateDoc, collection, query, where, getDocs, onSnapshot, Query, Timestamp } from 'firebase/firestore';
import { FirebaseService } from './firebaseService';
import { Booking, HeatmapData, Filters } from '../types';

export class BookingService {
  private static instance: BookingService;
  private firebaseService: FirebaseService;

  private constructor() {
    this.firebaseService = FirebaseService.getInstance();
  }

  public static getInstance(): BookingService {
    if (!BookingService.instance) {
      BookingService.instance = new BookingService();
    }
    return BookingService.instance;
  }

  public async createBooking(bookingData: Omit<Booking, 'id'>): Promise<Booking> {
    const db = this.firebaseService.getFirestore();
    const docRef = await addDoc(collection(db, 'bookings'), bookingData);
    return { id: docRef.id, ...bookingData };
  }

  public async updateBookingStatus(id: string, status: Booking['status']): Promise<void> {
    const db = this.firebaseService.getFirestore();
    await updateDoc(doc(db, 'bookings', id), {
      status,
      updatedAt: new Date()
    });
  }

  public async updateBookingPlayers(id: string, players: string[]): Promise<void> {
    const db = this.firebaseService.getFirestore();
    await updateDoc(doc(db, 'bookings', id), {
      players,
      updatedAt: new Date()
    });
  }

  public async getBookingsByUser(userId: string): Promise<Booking[]> {
    const db = this.firebaseService.getFirestore();
    const q = query(
      collection(db, 'bookings'),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Booking[];
  }

  public async getBookingsByCourt(courtId: string): Promise<Booking[]> {
    const db = this.firebaseService.getFirestore();
    const q = query(
      collection(db, 'bookings'),
      where('courtId', '==', courtId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Booking[];
  }

  public async getBookingsForWeek(
    startDate: Date,
    endDate: Date,
    filters?: Filters
  ): Promise<Booking[]> {
    const db = this.firebaseService.getFirestore();
    const q = query(
      collection(db, 'bookings'),
      where('startTime', '>=', startDate),
      where('startTime', '<=', endDate),
      where('status', '==', 'active')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Booking[];
  }

  public subscribeToBookings(
    startDate: Date,
    endDate: Date,
    callback: (bookings: Booking[]) => void,
    filters?: Filters
  ): () => void {
    const db = this.firebaseService.getFirestore();
    let q: Query = query(
      collection(db, 'bookings'),
      where('startTime', '>=', Timestamp.fromDate(startDate)),
      where('startTime', '<=', Timestamp.fromDate(endDate)),
      where('status', '==', 'active')
    );

    if (filters) {
      if (filters.level !== 'any') {
        q = query(q, where('level', '==', filters.level));
      }
      if (filters.court !== 'any') {
        q = query(q, where('courtId', '==', filters.court));
      }
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const bookings = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        startTime: doc.data().startTime?.toDate ? doc.data().startTime.toDate() : doc.data().startTime,
        endTime: doc.data().endTime?.toDate ? doc.data().endTime.toDate() : doc.data().endTime,
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt,
      })) as Booking[];
      callback(bookings);
    });
    return unsubscribe;
  }
}